import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { en } from "@/i18n/en";
import { fr } from "@/i18n/fr";
import type { TranslationDict } from "@/i18n/en";
import { normalizeBackendUrl } from "@/lib/api";

export type Language = "en" | "fr";

const DICTS: Record<Language, TranslationDict> = { en, fr };

const STORAGE_KEY = "cpl_language";
const TOKEN_KEY = "token";
const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL as string | undefined);

function detectLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Language | null;
    if (stored === "en" || stored === "fr") return stored;
    const browser = navigator.language.toLowerCase();
    if (browser.startsWith("fr")) return "fr";
  } catch (err) {
    console.warn("[language] Failed to read stored language preference:", err);
  }
  return "en";
}

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language, options?: { persistRemote?: boolean }) => void;
  t: TranslationDict;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: "en",
  setLanguage: () => {},
  t: en,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(detectLanguage);

  const setLanguage = useCallback((lang: Language, options?: { persistRemote?: boolean }) => {
    setLanguageState(lang);
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (err) { console.warn("[language] Failed to persist language preference:", err); }
    if (options?.persistRemote === false) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    fetch(`${BACKEND_URL}/api/user/preferences`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uiLanguage: lang }),
    }).catch(() => {});
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: DICTS[language] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
